import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
  listFiles,
  renameFile,
  duplicateFile,
  softDeleteFile,
  FileRecord,
  FileSort,
} from "../../services/filesService";
import {
  fileEditorPath,
  newMindmapCanvasPath,
  resolveFilesBackTarget,
} from "../../data/cloudRouting";
import { FilesTopBar } from "./FilesTopBar";
import { FileGrid } from "./FileGrid";
import { FilesGridSkeleton } from "./FilesGridSkeleton";
import { EmptyState } from "./EmptyState";
import { SignInPrompt } from "./SignInPrompt";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";
import "./my-files.scss";

/**
 * Route-backed `/files` page: browse, search, sort, rename, duplicate and
 * soft-delete the signed-in user's saved mindmaps.
 *
 * Auth gating: `useAuth()`'s `session` starts `undefined` both before the
 * initial check resolves and when genuinely logged out, so this page needs
 * a separate signal for "the check has settled" to know when it's safe to
 * render the sign-in prompt (avoids flashing it at a returning logged-in
 * user). `authResolved` is that signal — it comes from `useAuth()` itself
 * (set once its own one-shot `getSession()` / `onAuthStateChange` settle),
 * the same flag `App.tsx` and `useShareFile.ts` already gate on. A second,
 * independent `getSession()` call here would race that one and could
 * resolve first while `session` is still stale, flashing the prompt anyway.
 */
export const MyFilesPage: React.FC = () => {
  const { session, authResolved } = useAuth();
  const navigate = useNavigate();
  // `key` is "default" only on the entry the document loaded with, i.e. nothing
  // in this document was navigated to in-app.
  const { key: locationKey } = useLocation();

  const isSignedIn = authResolved && !!session;

  const [files, setFiles] = useState<FileRecord[] | null>(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<FileSort>("updated");
  const [error, setError] = useState("");
  const [fileToDelete, setFileToDelete] = useState<FileRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Every refresh() bumps this and captures the value; a response is only
  // applied if it's still the latest request. Without it, a fast sort toggle
  // races two listFiles() calls and the last-to-*resolve* (not last-issued)
  // wins, so the grid can settle on stale, out-of-order results.
  const requestIdRef = useRef(0);
  const refresh = useCallback(async (nextSort: FileSort) => {
    const requestId = ++requestIdRef.current;
    try {
      setError("");
      const rows = await listFiles({ sort: nextSort });
      if (requestId !== requestIdRef.current) {
        return; // a newer refresh superseded this one — ignore the stale result
      }
      setFiles(rows);
    } catch (err: any) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      setError(err?.message || "Could not load your files.");
      setFiles([]);
    }
  }, []);

  useEffect(() => {
    if (!isSignedIn) {
      return;
    }
    refresh(sort);
  }, [isSignedIn, sort, refresh]);

  const handleOpen = useCallback(
    (id: string) => navigate(fileEditorPath(id)),
    [navigate],
  );

  // Must mean the same thing as ☰ → New mindmap: enter create-on-first-edit, so
  // the first stroke makes a file. Plain `/` (the scratch canvas, where drawing
  // creates nothing) is only right for a signed-out user, who has no files.
  const handleNewMindmap = useCallback(
    () => navigate(isSignedIn ? newMindmapCanvasPath() : "/"),
    [isSignedIn, navigate],
  );

  const handleBack = useCallback(() => {
    if (
      resolveFilesBackTarget({
        hasRouterHistory: locationKey !== "default",
        referrer: typeof document !== "undefined" ? document.referrer : "",
        origin: typeof window !== "undefined" ? window.location.origin : "",
      }) === "back"
    ) {
      navigate(-1);
      return;
    }
    navigate("/");
  }, [locationKey, navigate]);

  const handleRename = useCallback(
    async (id: string, title: string) => {
      const previous = files;
      // Optimistic update so the UI feels instant; reconciled below.
      setFiles(
        (current) =>
          current?.map((f) => (f.id === id ? { ...f, title } : f)) ?? current,
      );
      try {
        await renameFile(id, title);
        setError("");
      } catch (err: any) {
        setError(err?.message || "Could not rename the file.");
        setFiles(previous ?? null);
      }
    },
    [files],
  );

  const handleDuplicate = useCallback(
    async (id: string) => {
      try {
        await duplicateFile(id);
        await refresh(sort);
      } catch (err: any) {
        setError(err?.message || "Could not duplicate the file.");
      }
    },
    [refresh, sort],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!fileToDelete) {
      return;
    }
    setDeleting(true);
    try {
      await softDeleteFile(fileToDelete.id);
      setFiles(
        (current) => current?.filter((f) => f.id !== fileToDelete.id) ?? null,
      );
      setFileToDelete(null);
      setError("");
    } catch (err: any) {
      setError(err?.message || "Could not delete the file.");
    } finally {
      setDeleting(false);
    }
  }, [fileToDelete]);

  const trimmedSearch = search.trim().toLowerCase();
  const visibleFiles = (files ?? []).filter((f) =>
    trimmedSearch ? f.title.toLowerCase().includes(trimmedSearch) : true,
  );

  // While the one-shot auth check is still in flight we don't yet know
  // whether to show the sign-in prompt or the file grid — render the page
  // shell (top bar) with a skeleton body instead of going blank, so there's
  // no flash from nothing to content.
  let body: React.ReactNode;
  if (!authResolved) {
    body = <FilesGridSkeleton />;
  } else if (!isSignedIn) {
    body = <SignInPrompt />;
  } else if (files === null) {
    body = <FilesGridSkeleton />;
  } else if (visibleFiles.length === 0) {
    body = (
      <EmptyState filtered={files.length > 0} onNewMindmap={handleNewMindmap} />
    );
  } else {
    body = (
      <FileGrid
        files={visibleFiles}
        accessToken={session?.access_token}
        onOpen={handleOpen}
        onRename={handleRename}
        onDuplicate={handleDuplicate}
        onDeleteRequest={setFileToDelete}
      />
    );
  }

  return (
    // The global stylesheet (index.html) locks `html, body` to
    // `height:100%; overflow:hidden` for the canvas app, so this page needs its
    // own scroll container or overflowing cards get clipped and unreachable.
    <div
      style={{
        height: "100vh",
        overflowY: "auto",
        background: "var(--mf-bg)",
        color: "var(--mf-text)",
      }}
    >
      <FilesTopBar
        search={search}
        onSearchChange={setSearch}
        sort={sort}
        onSortChange={setSort}
        onNewMindmap={handleNewMindmap}
        onBack={handleBack}
      />

      {error && (
        <p
          role="alert"
          style={{
            margin: "1rem 2rem 0",
            color: "var(--mf-danger)",
            background: "var(--mf-danger-bg)",
            borderRadius: "0.375rem",
            padding: "0.5rem 0.75rem",
            fontSize: "0.85rem",
          }}
        >
          {error}
        </p>
      )}

      {body}

      {fileToDelete && (
        <DeleteConfirmDialog
          file={fileToDelete}
          busy={deleting}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setFileToDelete(null)}
        />
      )}
    </div>
  );
};

export default MyFilesPage;
